import { createSlice } from "@reduxjs/toolkit";

/**
 * Initial state for UI slice
 */
const initialState = {
  sidebarOpen: false,
  notifications: [],
  activeModal: null,
  modalData: null,
  theme: localStorage.getItem("theme") || "light",
  language: localStorage.getItem("language") || "en",
  confirmDialog: {
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    onConfirm: null,
    severity: "warning",
  },
};

/**
 * UI slice for Redux
 * Handles UI-related state such as sidebar, notifications, modals, theme, and language
 */
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    /**
     * Toggle sidebar open/closed
     * @param {object} state - Current state
     */
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    /**
     * Set sidebar state
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing isOpen boolean
     */
    setSidebar: (state, action) => {
      state.sidebarOpen = action.payload;
    },

    /**
     * Add a notification
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing notification object
     */
    addNotification: (state, action) => {
      state.notifications.push({
        id: Date.now(),
        ...action.payload,
      });
    },

    /**
     * Remove a notification by ID
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing notification ID
     */
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },

    /**
     * Clear all notifications
     * @param {object} state - Current state
     */
    clearNotifications: (state) => {
      state.notifications = [];
    },

    /**
     * Open a modal
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing modal name and data
     */
    openModal: (state, action) => {
      state.activeModal = action.payload.name;
      state.modalData = action.payload.data || null;
    },

    /**
     * Close the active modal
     * @param {object} state - Current state
     */
    closeModal: (state) => {
      state.activeModal = null;
      state.modalData = null;
    },

    /**
     * Set theme (light/dark)
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing theme name
     */
    setTheme: (state, action) => {
      state.theme = action.payload;
      localStorage.setItem("theme", action.payload);
    },

    /**
     * Set language
     * @param {object} state - Current state
     * @param {object} action - Action with payload containing language code
     */
    setLanguage: (state, action) => {
      state.language = action.payload;
      localStorage.setItem("language", action.payload);
    },

    openConfirmDialog: (state, action) => {
      state.confirmDialog = {
        ...state.confirmDialog,
        open: true,
        ...action.payload,
      };
    },
    closeConfirmDialog: (state) => {
      state.confirmDialog = {
        ...initialState.confirmDialog,
        open: false,
      };
    },
  },
});

export const {
  toggleSidebar,
  setSidebar,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setTheme,
  setLanguage,
  openConfirmDialog,
  closeConfirmDialog,
} = uiSlice.actions;

export default uiSlice.reducer;
